<template>
    <div class="carousel-editor">
        <h4>Carousel Cards</h4>
        <p class="description">
            These cards will appear as a second message after the general body text. Each card must have an image/video header and at least one button (up to 2 buttons). Card body text is optional and supports variables. You can add up to 10 cards.
        </p>

        <!-- Carousel Cards -->
        <div class="carousel-cards-section">
            <div
                v-for="(card, cardIndex) of cards"
                :key="cardIndex"
                class="carousel-card-item"
            >
                <div class="carousel-card-header">
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <h5>Card {{ cardIndex + 1 }}</h5>
                        <div class="card-order-buttons">
                            <a-button 
                                size="small" 
                                @click="moveCardUp(cardIndex)"
                                :disabled="cardIndex === 0"
                                title="Move up"
                            >
                                <a-icon type="arrow-up" />
                            </a-button>
                            <a-button 
                                size="small" 
                                @click="moveCardDown(cardIndex)"
                                :disabled="cardIndex === cards.length - 1"
                                title="Move down"
                            >
                                <a-icon type="arrow-down" />
                            </a-button>
                        </div>
                    </div>
                    <a-button type="link" @click="removeCard(cardIndex)">
                        <a-icon type="delete" /> Remove
                    </a-button>
                </div>

                <!-- Card Header (Image/Video) -->
                <div class="form-group">
                    <label>Card Header <span class="required">*</span></label>
                    <a-select v-model="card.headerFormat" style="width: 150px; margin-bottom: 8px">
                        <a-select-option value="IMAGE">Image</a-select-option>
                        <a-select-option value="VIDEO">Video</a-select-option>
                    </a-select>

                    <!-- Media Upload -->
                    <div class="media-upload-section">
                        <div class="media-preview-box" v-if="card.media">
                            <img
                                v-if="card.headerFormat === 'IMAGE'"
                                :src="card.media.data"
                                alt="Preview"
                            />
                            <video
                                v-else-if="card.headerFormat === 'VIDEO'"
                                :src="card.media.data"
                                controls
                            />
                            <a-button type="link" class="remove-media-btn" @click="removeCardMedia(cardIndex)">
                                <a-icon type="delete" />
                            </a-button>
                            <div class="upload-status" v-if="card.uploading">
                                <a-spin size="small" /> Uploading to Meta...
                            </div>
                            <div class="upload-success" v-else-if="card.exampleMedia">
                                <a-icon type="check-circle" /> Uploaded successfully
                            </div>
                        </div>
                        <div class="media-upload-box" v-else>
                            <input
                                type="file"
                                :accept="card.headerFormat === 'IMAGE' ? 'image/jpeg, image/png' : 'video/mp4'"
                                @change="handleCardFileUpload($event, cardIndex)"
                                class="file-input"
                            />
                            <div class="upload-content">
                                <a-icon type="cloud-upload" class="upload-icon" />
                                <div class="upload-text">Drag and drop to upload</div>
                                <div class="upload-link">Or choose files on your device</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Card Body (Optional) -->
                <div class="form-group">
                    <label>Card Body • Optional</label>
                    <div class="body-editor-wrapper">
                        <a-textarea
                            :value="card.body"
                            @input="updateCardBody(cardIndex, $event.target.value)"
                            placeholder="Enter card body text (optional)"
                            :rows="3"
                            :maxLength="160"
                        />
                        <div class="editor-toolbar">
                            <a-button size="small" @click="addCardVariable(cardIndex)">
                                <a-icon type="plus" /> Add Variable
                            </a-button>
                            <span class="char-count">{{ (card.body || '').length }}/160</span>
                        </div>
                    </div>
                    
                    <!-- Card Body Variables -->
                    <div v-if="card.bodyVariables && card.bodyVariables.length > 0" class="variables-section">
                        <div v-for="(variable, varIndex) in card.bodyVariables" :key="varIndex" class="variable-example">
                            <span class="variable-tag">{{ variable.variable }}</span>
                            <a-input
                                :value="variable.example"
                                @input="updateCardVariableExample(cardIndex, varIndex, $event.target.value)"
                                placeholder="Sample value"
                                style="flex: 1"
                            />
                        </div>
                    </div>
                </div>

                <!-- Card Buttons -->
                <div class="form-group">
                    <label>Buttons <span class="required">*</span> (at least 1, up to 2)</label>
                    <div class="buttons-list">
                        <div
                            v-for="(button, buttonIndex) of card.buttons"
                            :key="buttonIndex"
                            class="button-item-wrapper"
                        >
                            <div class="button-item">
                                <a-select v-model="button.type" style="width: 150px">
                                    <a-select-option value="QUICK_REPLY">Quick Reply</a-select-option>
                                    <a-select-option value="URL">Visit Website</a-select-option>
                                    <a-select-option value="PHONE_NUMBER">Call Phone</a-select-option>
                                </a-select>
                                <a-input
                                    v-model="button.text"
                                    placeholder="Button text"
                                    style="flex: 1"
                                    :maxLength="25"
                                />
                                <a-input
                                    v-if="button.type === 'URL'"
                                    v-model="button.url"
                                    :placeholder="'https://example.com/' + '{{' + '1' + '}}'"
                                    style="flex: 1"
                                />
                                <a-input
                                    v-if="button.type === 'PHONE_NUMBER'"
                                    v-model="button.phone_number"
                                    placeholder="+1234567890"
                                    style="flex: 1"
                                />
                                <a-button 
                                    type="link" 
                                    @click="removeCardButton(cardIndex, buttonIndex)"
                                    :disabled="card.buttons.length === 1"
                                    :style="{ visibility: card.buttons.length === 1 ? 'hidden' : 'visible' }"
                                >
                                    <a-icon type="delete" />
                                </a-button>
                            </div>
                            <!-- URL Button Example -->
                            <div
                                v-if="button.type === 'URL' && hasDynamicUrl(button.url)"
                                class="url-example-section"
                            >
                                <div class="url-example-info">
                                    <a-icon type="info-circle" />
                                    <span>{{ $t("whatsapp_business_api.dynamicUrlSampleNote") }}</span>
                                </div>
                                <div class="url-example-input">
                                    <span class="variable-tag">{{1}}</span>
                                    <a-input
                                        v-model="button.urlExample"
                                        placeholder="example_value"
                                        style="flex: 1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <a-dropdown :trigger="['click']" v-if="card.buttons.length < 2">
                        <a-button size="small">
                            <a-icon type="plus" /> Add button
                        </a-button>
                        <a-menu slot="overlay">
                            <a-menu-item @click="addCardButton(cardIndex, 'QUICK_REPLY')">
                                <a-icon type="message" /> Quick Reply
                            </a-menu-item>
                            <a-menu-item @click="addCardButton(cardIndex, 'URL')">
                                <a-icon type="link" /> Visit Website
                            </a-menu-item>
                            <a-menu-item @click="addCardButton(cardIndex, 'PHONE_NUMBER')">
                                <a-icon type="phone" /> Call Phone
                            </a-menu-item>
                        </a-menu>
                    </a-dropdown>
                </div>
            </div>
        </div>

        <a-button @click="addCard" v-if="cards.length < 10" type="dashed" block>
            <a-icon type="plus" /> Add Card
        </a-button>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "CarouselEditor",
    props: {
        cards: { type: Array, required: true },
    },
    methods: {
        hasDynamicUrl(url) {
            return url && /\{\{\d+\}\}/.test(url);
        },
        addCard() {
            this.$emit('add-card');
        },
        removeCard(index) {
            this.$emit('remove-card', index);
        },
        moveCardUp(index) {
            this.$emit('move-card-up', index);
        },
        moveCardDown(index) {
            this.$emit('move-card-down', index);
        },
        updateCardBody(cardIndex, value) {
            this.$emit('update-card-body', { cardIndex, value });
        },
        addCardVariable(cardIndex) {
            this.$emit('add-card-variable', cardIndex);
        },
        updateCardVariableExample(cardIndex, variableIndex, value) {
            this.$emit('update-card-variable-example', { cardIndex, variableIndex, value });
        },
        addCardButton(cardIndex, buttonType) {
            this.$emit('add-card-button', { cardIndex, buttonType });
        },
        removeCardButton(cardIndex, buttonIndex) {
            this.$emit('remove-card-button', { cardIndex, buttonIndex });
        },
        handleCardFileUpload(event, cardIndex) {
            this.$emit('card-file-upload', { event, cardIndex });
        },
        removeCardMedia(cardIndex) {
            this.$emit('remove-card-media', cardIndex);
        },
    },
});
</script>

<style scoped lang="scss">
.carousel-editor {
    h4 {
        margin: 0 0 8px 0;
        font-weight: 600;
    }

    .description {
        color: #666;
        font-size: 14px;
        margin-bottom: 20px;
    }

    .carousel-cards-section {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-bottom: 20px;

        .carousel-card-item {
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            padding: 20px;
            background: #fafafa;

            .carousel-card-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;
                padding-bottom: 12px;
                border-bottom: 1px solid #e8e8e8;

                h5 {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 600;
                    color: #333;
                }

                .card-order-buttons {
                    display: flex;
                    gap: 4px;

                    .ant-btn {
                        padding: 0 8px;
                        height: 24px;
                        line-height: 22px;
                    }
                }

                .ant-btn-link {
                    color: #ff4d4f;
                    padding: 0;

                    &:hover {
                        color: #ff7875;
                    }
                }
            }

            .form-group {
                background: white;
                padding: 16px;
                border-radius: 6px;
                margin-bottom: 16px;

                &:last-child {
                    margin-bottom: 0;
                }

                label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 500;

                    .required {
                        color: #ff4d4f;
                    }
                }

                .body-editor-wrapper {
                    .editor-toolbar {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 8px;

                        .char-count {
                            font-size: 12px;
                            color: #999;
                        }
                    }
                }

                .variables-section {
                    margin-top: 12px;
                    padding: 12px;
                    background: #fffbe6;
                    border-radius: 6px;
                    border: 1px solid #ffe58f;

                    .variable-example {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 8px;

                        &:last-child {
                            margin-bottom: 0;
                        }

                        .variable-tag {
                            background: #1890ff;
                            color: white;
                            padding: 4px 8px;
                            border-radius: 4px;
                            font-family: monospace;
                            font-size: 12px;
                            font-weight: 500;
                            min-width: 50px;
                            text-align: center;
                        }
                    }
                }
            }

            .buttons-list {
                display: flex;
                flex-direction: column;
                gap: 12px;
                margin-bottom: 12px;

                .button-item-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .button-item {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .url-example-section {
                    margin-left: 162px;
                    padding: 12px;
                    background: #fffbe6;
                    border-radius: 6px;
                    border: 1px solid #ffe58f;

                    .url-example-info {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 8px;
                        font-size: 13px;
                        color: #8c8c8c;
                    }

                    .url-example-input {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .variable-tag {
                            background: #1890ff;
                            color: white;
                            padding: 4px 8px;
                            border-radius: 4px;
                            font-family: monospace;
                            font-size: 12px;
                        }
                    }
                }
            }

            .media-upload-section {
                .media-preview-box {
                    position: relative;
                    border: 1px solid #e8e8e8;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #fafafa;
                    padding: 12px;

                    img, video {
                        width: 100%;
                        max-height: 200px;
                        object-fit: cover;
                        border-radius: 6px;
                    }

                    .remove-media-btn {
                        position: absolute;
                        top: 20px;
                        right: 20px;
                        background: rgba(0, 0, 0, 0.5);
                        color: white;
                        border-radius: 50%;

                        &:hover {
                            background: rgba(255, 77, 79, 0.8);
                        }
                    }

                    .upload-status, .upload-success {
                        margin-top: 12px;
                        padding: 8px 12px;
                        background: white;
                        border-radius: 6px;
                        font-size: 13px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                }

                .media-upload-box {
                    position: relative;
                    border: 2px dashed #d9d9d9;
                    border-radius: 8px;
                    padding: 40px 20px;
                    text-align: center;
                    background: #fafafa;

                    .file-input {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        opacity: 0;
                        cursor: pointer;
                    }

                    .upload-content {
                        pointer-events: none;

                        .upload-icon {
                            font-size: 48px;
                            color: #1890ff;
                        }

                        .upload-text {
                            font-size: 14px;
                            color: #333;
                            margin-top: 12px;
                        }

                        .upload-link {
                            font-size: 13px;
                            color: #1890ff;
                            margin-top: 4px;
                        }
                    }
                }
            }
        }
    }
}
</style>
